import { observable } from "mobx"

import { BaseModel } from "./base.model"

interface LegalSection {
  id: string
  title: string
  text: string
  slug: string
}

export class LegalModel extends BaseModel {
  @observable cookiesTitle: string = ""
  @observable cookiesSections: LegalSection[] = []
  @observable cookiesEffectiveDate: string = ""
  @observable cookiesVersions: string[] = []

  @observable recurringTitle: string = ""
  @observable recurringSections: LegalSection[] = []
  @observable recurringEffectiveDate: string = ""
  @observable recurringVersions: string[] = []

  @observable refundTitle: string = ""
  @observable refundSections: LegalSection[] = []
  @observable refundEffectiveDate: string = ""
  @observable refundVersions: string[] = []

  @observable privacyTitle: string = ""
  @observable privacySections: LegalSection[] = []
  @observable privacyEffectiveDate: string = ""
  @observable privacyVersions: string[] = []

  @observable termsTitle: string = ""
  @observable termsSections: LegalSection[] = []
  @observable termsEffectiveDate: string = ""
  @observable termsVersions: string[] = []

  @observable consumerHealthTitle: string = ""
  @observable consumerHealthSections: LegalSection[] = []
  @observable consumerHealthEffectiveDate: string = ""
  @observable consumerHealthVersions: string[] = []

  @observable californiaPrivacyTitle: string = ""
  @observable californiaPrivacySections: LegalSection[] = []
  @observable californiaPrivacyEffectiveDate: string = ""
  @observable californiaPrivacyVersions: string[] = []

  @observable reportingContentTitle: string = ""
  @observable reportingContentSections: LegalSection[] = []
  @observable reportingContentEffectiveDate: string = ""
  @observable reportingContentVersions: string[] = []

  @observable privacyForCaliforniaTitle: string = ""
  @observable privacyForCaliforniaSections: LegalSection[] = []
  @observable privacyForCaliforniaEffectiveDate: string = ""
  @observable privacyForCaliforniaVersions: string[] = []

  @observable childSafetyTitle: string = ""
  @observable childSafetySections: LegalSection[] = []
  @observable childSafetyEffectiveDate: string = ""
  @observable childSafetyVersions: string[] = []

  @observable valentinesDayTermsTitle: string = ""
  @observable valentinesDayTermsSections: LegalSection[] = []
  @observable valentinesDayTermsEffectiveDate: string = ""
  @observable valentinesDayTermsVersions: string[] = []

  @observable coloradoSafetyTitle: string = ""
  @observable coloradoSafetySections: LegalSection[] = []
  @observable coloradoSafetyEffectiveDate: string = ""
  @observable coloradoSafetyVersions: string[] = []

  @observable threateningBehaviorTitle: string = ""
  @observable threateningBehaviorSections: LegalSection[] = []
  @observable threateningBehaviorEffectiveDate: string = ""
  @observable threateningBehaviorVersions: string[] = []

  @observable accountDeletionTitle: string = ""
  @observable accountDeletionText: string = ""

  getCookiesPageData = async (date?: string, countryCode?: string) => {
    const locale = this.Models.GlobalModel.lang !== "ru" ? "en" : "ru"
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getCookiesPageData(
      locale,
      date,
      countryCode,
    )
    this.applyData({
      cookiesTitle: data.title,
      cookiesSections: data.sections,
      cookiesEffectiveDate: data.effective_date,
      cookiesVersions: versions || [],
    })
  }

  getRecurringPageData = async (date?: string, countryCode?: string) => {
    const locale = this.Models.GlobalModel.lang !== "ru" ? "en" : "ru"
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getRecurringPageData(
      locale,
      date,
      countryCode,
    )
    this.applyData({
      recurringTitle: data.title,
      recurringSections: data.sections,
      recurringEffectiveDate: data.effective_date,
      recurringVersions: versions || [],
    })
  }

  getRefundPageData = async (date?: string, countryCode?: string) => {
    const locale = this.Models.GlobalModel.lang !== "ru" ? "en" : "ru"
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getRefundPageData(
      locale,
      date,
      countryCode,
    )
    this.applyData({
      refundTitle: data.title,
      refundSections: data.sections,
      refundEffectiveDate: data.effective_date,
      refundVersions: versions || [],
    })
  }

  getPrivacyPageData = async (date?: string, countryCode?: string) => {
    const locale = this.Models.GlobalModel.lang !== "ru" ? "en" : "ru"
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getPrivacyLastOrByDate(
      locale,
      date,
      countryCode,
    )
    this.applyData({
      privacyTitle: data.title,
      privacySections: data.sections,
      privacyEffectiveDate: data.effective_date,
      privacyVersions: versions || [],
    })
  }

  getTermsPageData = async (date?: string, countryCode?: string) => {
    const locale = this.Models.GlobalModel.lang !== "ru" ? "en" : "ru"
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getTermsLastOrByDate(
      locale,
      date,
      countryCode,
    )
    this.applyData({
      termsTitle: data.title,
      termsSections: data.sections,
      termsEffectiveDate: data.effective_date,
      termsVersions: versions || [],
    })
  }

  getConsumerHealthPageData = async (date?: string) => {
    const locale = "en"
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getConsumerHealthLastOrByDate(
      locale,
      date,
    )
    this.applyData({
      consumerHealthTitle: data.title,
      consumerHealthSections: data.sections,
      consumerHealthEffectiveDate: data.effective_date,
      consumerHealthVersions: versions || [],
    })
  }

  getCaliforniaPrivacyPageData = async (date?: string) => {
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getCaliforniaPrivacyPageData(date)
    this.applyData({
      californiaPrivacyTitle: data.title,
      californiaPrivacySections: data.sections,
      californiaPrivacyEffectiveDate: data.effective_date,
      californiaPrivacyVersions: versions || [],
    })
  }

  getAccountDeletionPageData = async () => {
    try {
      const {
        text,
        title,
      } = await this.Providers.LegalProvider.getAccountDeletionPageData()
      this.applyData({
        accountDeletionTitle: title,
        accountDeletionText: text,
      })
    } catch {}
  }

  getReportingContentPageData = async (date?: string, countryCode?: string) => {
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getReportingContentPageData(
      "en",
      date,
      countryCode,
    )
    this.applyData({
      reportingContentTitle: data.title,
      reportingContentSections: data.sections,
      reportingContentEffectiveDate: data.effective_date,
      reportingContentVersions: versions || [],
    })
  }

  getPrivacyForCaliforniaPageData = async (
    date?: string,
    countryCode?: string,
  ) => {
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getPrivacyPolicyForCaliforniaPageData(
      "en",
      date,
      countryCode,
    )
    this.applyData({
      privacyForCaliforniaTitle: data.title,
      privacyForCaliforniaSections: data.sections,
      privacyForCaliforniaEffectiveDate: data.effective_date,
      privacyForCaliforniaVersions: versions || [],
    })
  }

  getChildSafetyPageData = async (date?: string, countryCode?: string) => {
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getChildSafetyPageData(
      "en",
      date,
      countryCode,
    )
    this.applyData({
      childSafetyTitle: data.title,
      childSafetySections: data.sections,
      childSafetyEffectiveDate: data.effective_date,
      childSafetyVersions: versions || [],
    })
  }

  getValentinesDayPageData = async () => {
    const locale = this.Models.GlobalModel.lang !== "fr" ? "en" : "fr"
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getValentinesDayTermsPageData(locale)
    this.applyData({
      valentinesDayTermsTitle: data.title,
      valentinesDayTermsSections: data.sections,
      valentinesDayTermsEffectiveDate: data.effective_date,
      valentinesDayTermsVersions: versions || [],
    })
  }

  getColoradoSafetyPageData = async (date?: string) => {
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getColoradoSafetyPageData(date)
    this.applyData({
      coloradoSafetyTitle: data.title,
      coloradoSafetySections: data.sections,
      coloradoSafetyEffectiveDate: data.effective_date,
      coloradoSafetyVersions: versions || [],
    })
  }

  getThreateningBehaviorPageData = async (date?: string) => {
    const {
      data,
      versions,
    } = await this.Providers.LegalProvider.getThreateningBehaviorPageData(date)
    this.applyData({
      threateningBehaviorTitle: data.title,
      threateningBehaviorSections: data.sections,
      threateningBehaviorEffectiveDate: data.effective_date,
      threateningBehaviorVersions: versions || [],
    })
  }

  hydrate = (data) => {
    this.applyData({
      cookiesTitle: data.cookiesTitle,
      cookiesSections: data.cookiesSections,
      cookiesEffectiveDate: data.cookiesEffectiveDate,
      cookiesVersions: data.cookiesVersions,
      recurringTitle: data.recurringTitle,
      recurringSections: data.recurringSections,
      recurringEffectiveDate: data.recurringEffectiveDate,
      recurringVersions: data.recurringVersions,
      refundTitle: data.refundTitle,
      refundSections: data.refundSections,
      refundEffectiveDate: data.refundEffectiveDate,
      refundVersions: data.refundVersions,
      privacyTitle: data.privacyTitle,
      privacySections: data.privacySections,
      privacyEffectiveDate: data.privacyEffectiveDate,
      privacyVersions: data.privacyVersions,
      termsTitle: data.termsTitle,
      termsSections: data.termsSections,
      termsEffectiveDate: data.termsEffectiveDate,
      termsVersions: data.termsVersions,
      consumerHealthTitle: data.consumerHealthTitle,
      consumerHealthSections: data.consumerHealthSections,
      consumerHealthEffectiveDate: data.consumerHealthEffectiveDate,
      consumerHealthVersions: data.consumerHealthVersions,
      californiaPrivacyTitle: data.californiaPrivacyTitle,
      californiaPrivacySections: data.californiaPrivacySections,
      californiaPrivacyEffectiveDate: data.californiaPrivacyEffectiveDate,
      californiaPrivacyVersions: data.californiaPrivacyVersions,
      accountDeletionTitle: data.accountDeletionTitle,
      accountDeletionText: data.accountDeletionText,
      reportingContentTitle: data.reportingContentTitle,
      reportingContentSections: data.reportingContentSections,
      reportingContentEffectiveDate: data.reportingContentEffectiveDate,
      reportingContentVersions: data.reportingContentVersions,
      privacyForCaliforniaTitle: data.privacyForCaliforniaTitle,
      privacyForCaliforniaSections: data.privacyForCaliforniaSections,
      privacyForCaliforniaEffectiveDate: data.privacyForCaliforniaEffectiveDate,
      privacyForCaliforniaVersions: data.privacyForCaliforniaVersions,
      childSafetyTitle: data.childSafetyTitle,
      childSafetySections: data.childSafetySections,
      childSafetyEffectiveDate: data.childSafetyEffectiveDate,
      childSafetyVersions: data.childSafetyVersions,
      valentinesDayTermsTitle: data.valentinesDayTermsTitle,
      valentinesDayTermsSections: data.valentinesDayTermsSections,
      valentinesDayTermsEffectiveDate: data.valentinesDayTermsEffectiveDate,
      valentinesDayTermsVersions: data.valentinesDayTermsVersions,
      coloradoSafetyTitle: data.coloradoSafetyTitle,
      coloradoSafetySections: data.coloradoSafetySections,
      coloradoSafetyEffectiveDate: data.coloradoSafetyEffectiveDate,
      coloradoSafetyVersions: data.coloradoSafetyVersions,
      threateningBehaviorTitle: data.threateningBehaviorTitle,
      threateningBehaviorSections: data.threateningBehaviorSections,
      threateningBehaviorEffectiveDate: data.threateningBehaviorEffectiveDate,
      threateningBehaviorVersions: data.threateningBehaviorVersions,
    })
  }

  serialize = () => {
    return {
      cookiesTitle: this.cookiesTitle,
      cookiesSections: this.cookiesSections,
      cookiesEffectiveDate: this.cookiesEffectiveDate,
      cookiesVersions: this.cookiesVersions,
      recurringTitle: this.recurringTitle,
      recurringSections: this.recurringSections,
      recurringEffectiveDate: this.recurringEffectiveDate,
      recurringVersions: this.recurringVersions,
      refundTitle: this.refundTitle,
      refundSections: this.refundSections,
      refundEffectiveDate: this.refundEffectiveDate,
      refundVersions: this.refundVersions,
      privacyTitle: this.privacyTitle,
      privacySections: this.privacySections,
      privacyEffectiveDate: this.privacyEffectiveDate,
      privacyVersions: this.privacyVersions,
      termsTitle: this.termsTitle,
      termsSections: this.termsSections,
      termsEffectiveDate: this.termsEffectiveDate,
      termsVersions: this.termsVersions,
      consumerHealthTitle: this.consumerHealthTitle,
      consumerHealthSections: this.consumerHealthSections,
      consumerHealthEffectiveDate: this.consumerHealthEffectiveDate,
      consumerHealthVersions: this.consumerHealthVersions,
      californiaPrivacyTitle: this.californiaPrivacyTitle,
      californiaPrivacySections: this.californiaPrivacySections,
      californiaPrivacyEffectiveDate: this.californiaPrivacyEffectiveDate,
      californiaPrivacyVersions: this.californiaPrivacyVersions,
      accountDeletionTitle: this.accountDeletionTitle,
      accountDeletionText: this.accountDeletionText,
      reportingContentTitle: this.reportingContentTitle,
      reportingContentSections: this.reportingContentSections,
      reportingContentEffectiveDate: this.reportingContentEffectiveDate,
      reportingContentVersions: this.reportingContentVersions,
      privacyForCaliforniaTitle: this.privacyForCaliforniaTitle,
      privacyForCaliforniaSections: this.privacyForCaliforniaSections,
      privacyForCaliforniaEffectiveDate: this.privacyForCaliforniaEffectiveDate,
      privacyForCaliforniaVersions: this.privacyForCaliforniaVersions,
      childSafetyTitle: this.childSafetyTitle,
      childSafetySections: this.childSafetySections,
      childSafetyEffectiveDate: this.childSafetyEffectiveDate,
      childSafetyVersions: this.childSafetyVersions,
      valentinesDayTermsTitle: this.valentinesDayTermsTitle,
      valentinesDayTermsSections: this.valentinesDayTermsSections,
      valentinesDayTermsEffectiveDate: this.valentinesDayTermsEffectiveDate,
      valentinesDayTermsVersions: this.valentinesDayTermsVersions,
      coloradoSafetyTitle: this.coloradoSafetyTitle,
      coloradoSafetySections: this.coloradoSafetySections,
      coloradoSafetyEffectiveDate: this.coloradoSafetyEffectiveDate,
      coloradoSafetyVersions: this.coloradoSafetyVersions,
      threateningBehaviorTitle: this.threateningBehaviorTitle,
      threateningBehaviorSections: this.threateningBehaviorSections,
      threateningBehaviorEffectiveDate: this.threateningBehaviorEffectiveDate,
      threateningBehaviorVersions: this.threateningBehaviorVersions,
    }
  }
}
